import { cleanPermalink } from '../../../statistics/lib/top-videos';
import type { jsPDF } from 'jspdf';

import { FONT_METRICS, type FontKey, type LogoAnchor } from './layout';

/**
 * Примитивы рисования поверх jsPDF в координатах макета.
 *
 * Текст ставится так же, как в браузере: блок высотой lineHeight, строка по
 * его центру, базовая линия — от метрик шрифта. Межбуквенный интервал —
 * оператор Tc, ширина считается с ним же, иначе центровка съезжает.
 */

export type PdfImage = {
  data: Uint8Array;
  format: 'PNG' | 'JPEG';
  width: number;
  height: number;
  /** Одинаковый alias — картинка кладётся в файл один раз на все слайды. */
  alias?: string;
};

export type Box = { x: number; y: number; w: number; h: number };

export function registerFonts(doc: jsPDF, fonts: Record<FontKey, string>): void {
  for (const [key, base64] of Object.entries(fonts) as Array<[FontKey, string]>) {
    const file = `${key}.ttf`;
    doc.addFileToVFS(file, base64);
    doc.addFont(file, key, 'normal');
  }
}

function selectFont(doc: jsPDF, font: FontKey, size: number, tracking: number): void {
  doc.setFont(font, 'normal');
  doc.setFontSize(size);
  doc.setCharSpace(tracking);
}

/** Ширина строки с интервалом между знаками (после последнего — не в счёт). */
export function measureText(
  doc: jsPDF,
  text: string,
  font: FontKey,
  size: number,
  tracking = 0,
): number {
  // getTextWidth в jsPDF 4 сам прибавляет текущий charSpace (и после последнего
  // знака тоже), поэтому меряем без него и добавляем интервал сами.
  selectFont(doc, font, size, 0);
  const chars = Array.from(text).length;
  return doc.getTextWidth(text) + tracking * Math.max(0, chars - 1);
}

export type TextStyle = {
  font: FontKey;
  size: number;
  color: string;
  /** Высота строки; по умолчанию «normal» — ascent + descent шрифта. */
  lineHeight?: number;
  tracking?: number;
};

/** Базовая линия строки, у которой верх блока — top. */
export function baselineOf(style: TextStyle, top: number): number {
  const m = FONT_METRICS[style.font];
  const content = (m.ascent + m.descent) * style.size;
  const lineHeight = style.lineHeight ?? content;
  return top + (lineHeight - content) / 2 + m.ascent * style.size;
}

/** Рисует строку; x — левый край или центр. Возвращает левый край и ширину. */
export function drawText(
  doc: jsPDF,
  text: string,
  style: TextStyle,
  pos: { x: number; top: number; align?: 'left' | 'center' | 'right' },
): { left: number; width: number } {
  const tracking = style.tracking ?? 0;
  const width = measureText(doc, text, style.font, style.size, tracking);
  const left =
    pos.align === 'center' ? pos.x - width / 2 : pos.align === 'right' ? pos.x - width : pos.x;
  selectFont(doc, style.font, style.size, tracking);
  doc.setTextColor(style.color);
  doc.text(text, left, baselineOf(style, pos.top), { charSpace: tracking });
  doc.setCharSpace(0);
  return { left, width };
}

/** Строка по базовой линии: экран телефона мерим по скриншоту, а там она. */
export function drawTextBaseline(
  doc: jsPDF,
  text: string,
  style: TextStyle,
  pos: { x: number; baseline: number; align?: 'left' | 'center' | 'right' },
): void {
  const width = measureText(doc, text, style.font, style.size);
  const left =
    pos.align === 'center' ? pos.x - width / 2 : pos.align === 'right' ? pos.x - width : pos.x;
  selectFont(doc, style.font, style.size, 0);
  doc.setTextColor(style.color);
  doc.text(text, left, pos.baseline);
}

export function fillRect(doc: jsPDF, box: Box, color: string): void {
  doc.setFillColor(color);
  doc.rect(box.x, box.y, box.w, box.h, 'F');
}

export function fillRoundedRect(doc: jsPDF, box: Box, r: number, color: string, opacity = 1): void {
  if (opacity < 1) {
    doc.saveGraphicsState();
    doc.setGState(doc.GState({ opacity }));
  }
  doc.setFillColor(color);
  doc.roundedRect(box.x, box.y, box.w, box.h, r, r, 'F');
  if (opacity < 1) doc.restoreGraphicsState();
}

/** Обводка внутрь фигуры, как «Inside» в Figma. */
export function strokeRoundedRectInside(
  doc: jsPDF,
  box: Box,
  r: number,
  width: number,
  color: string,
  opacity = 1,
): void {
  const half = width / 2;
  if (opacity < 1) {
    doc.saveGraphicsState();
    // GState в jsPDF сам создаёт экземпляр, если вызван без new, — так и в типах.
    doc.setGState(doc.GState({ 'stroke-opacity': opacity }));
  }
  doc.setDrawColor(color);
  doc.setLineWidth(width);
  doc.roundedRect(
    box.x + half,
    box.y + half,
    box.w - width,
    box.h - width,
    Math.max(0, r - half),
    Math.max(0, r - half),
    'S',
  );
  if (opacity < 1) doc.restoreGraphicsState();
}

export function drawImage(doc: jsPDF, img: PdfImage, box: Box): void {
  doc.addImage(img.data, img.format, box.x, box.y, box.w, box.h, img.alias, 'FAST');
}

/** Заполняет скруглённую область с обрезкой — как object-fit: cover. */
export function drawImageCover(doc: jsPDF, img: PdfImage, box: Box, r: number): void {
  const scale = Math.max(box.w / img.width, box.h / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  doc.saveGraphicsState();
  doc.roundedRect(box.x, box.y, box.w, box.h, r, r, null);
  doc.clip();
  doc.discardPath();
  drawImage(doc, img, { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h });
  doc.restoreGraphicsState();
}

/** Вписывает картинку в рамку целиком (object-fit: contain) с привязкой по горизонтали. */
export function drawImageContain(doc: jsPDF, img: PdfImage, box: Box, anchor: LogoAnchor): void {
  const scale = Math.min(box.w / img.width, box.h / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  const x =
    anchor === 'left' ? box.x : anchor === 'right' ? box.x + box.w - w : box.x + (box.w - w) / 2;
  drawImage(doc, img, { x, y: box.y + (box.h - h) / 2, w, h });
}

export function addLink(doc: jsPDF, box: Box, url: string): void {
  if (!url) return;
  doc.link(box.x, box.y, box.w, box.h, { url: cleanPermalink(url) });
}
